import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  baseUrl: string;
  queryParams?: Record<string, string>;
}

export function Pagination({
  currentPage,
  totalPages,
  baseUrl,
  queryParams = {},
}: PaginationProps) {
  const getPageUrl = (page: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      ...queryParams,
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const pageNumbers = [];
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex items-center justify-center gap-3 my-16">
      {currentPage > 1 && (
        <Link href={getPageUrl(currentPage - 1)}>
          <Button variant="outline" size="default">
            <ChevronLeft className="h-5 w-5" />
            Previous
          </Button>
        </Link>
      )}

      <div className="flex gap-1.5">
        {startPage > 1 && (
          <>
            <Link href={getPageUrl(1)}>
              <Button variant="outline" size="default">
                1
              </Button>
            </Link>
            {startPage > 2 && <span className="px-2.5 py-1.5">...</span>}
          </>
        )}

        {pageNumbers.map((page) => (
          <Link key={page} href={getPageUrl(page)}>
            <Button
              variant={page === currentPage ? "default" : "outline"}
              size="default"
            >
              {page}
            </Button>
          </Link>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2.5 py-1.5">...</span>}
            <Link href={getPageUrl(totalPages)}>
              <Button variant="outline" size="default">
                {totalPages}
              </Button>
            </Link>
          </>
        )}
      </div>

      {currentPage < totalPages && (
        <Link href={getPageUrl(currentPage + 1)}>
          <Button variant="outline" size="default">
            Next
            <ChevronRight className="h-5 w-5" />
          </Button>
        </Link>
      )}

      <div className="ml-4 text-base text-muted-foreground">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
}
